import { Parser } from 'node-sql-parser';

export interface SqlValidationResult {
  isValid: boolean;
  error: string;
}

export class SqlValidator {
  private parser: Parser;
  private defaultDatabase: string;

  constructor(defaultDatabase = 'transactsql') {
    this.parser = new Parser();
    this.defaultDatabase = defaultDatabase;
  }

  /**
   * Preprocesses SQL Server-specific syntax to make it compatible with the parser
   * Handles:
   * - Table hints like (NOLOCK), (READPAST), (READUNCOMMITTED), etc. only when they appear after table names in FROM/JOIN clauses
   * - WITH table hints syntax
   * @param sql - The original SQL query string
   * @returns Preprocessed SQL string
   */
  private preprocessSqlServerSyntax(sql: string): string {
    let processedSql = sql;

    // More precise regex to remove table hints only in FROM and JOIN contexts
    // This pattern looks for FROM or JOIN followed by table name and table hint
    processedSql = processedSql.replace(
      /(FROM|JOIN)\s+(\w+(?:\.\w+)?)\s*\((?:NOLOCK|READPAST|READUNCOMMITTED|READCOMMITTED|REPEATABLEREAD|SERIALIZABLE|ROWLOCK|PAGLOCK|TABLOCK|TABLOCKX|UPDLOCK|XLOCK|HOLDLOCK|NOWAIT)\)/gi,
      '$1 $2',
    );

    // Handle WITH table hints after table names in FROM/JOIN clauses
    processedSql = processedSql.replace(
      /(FROM|JOIN)\s+(\w+(?:\.\w+)?)\s+WITH\s*\((?:NOLOCK|READPAST|READUNCOMMITTED|READCOMMITTED|REPEATABLEREAD|SERIALIZABLE|ROWLOCK|PAGLOCK|TABLOCK|TABLOCKX|UPDLOCK|XLOCK|HOLDLOCK|NOWAIT)(?:\s*,\s*(?:NOLOCK|READPAST|READUNCOMMITTED|READCOMMITTED|REPEATABLEREAD|SERIALIZABLE|ROWLOCK|PAGLOCK|TABLOCK|TABLOCKX|UPDLOCK|XLOCK|HOLDLOCK|NOWAIT))*\s*\)/gi,
      '$1 $2',
    );

    // Clean up extra whitespaces
    processedSql = processedSql.replace(/\s+/g, ' ').trim();

    return processedSql;
  }

  /**
   * Validates SQL syntax using node-sql-parser
   * @param sql - The SQL query string to validate
   * @param database - The database type to validate against (default: 'transactsql' for SQL Server)
   * @returns SqlValidationResult with validation status and error message
   */
  validateSyntax(sql: string, database?: string): SqlValidationResult {
    // Use provided database type or fall back to default
    const dbType = database || this.defaultDatabase;

    // Check if SQL is empty or only whitespace
    if (!sql.trim()) {
      return {
        isValid: false,
        error: 'SQL query cannot be empty',
      };
    }

    try {
      // First try without preprocessing for better performance
      this.parser.astify(sql, { database: dbType });
      return {
        isValid: true,
        error: '',
      };
    } catch (error: any) {
      // If failed, try with preprocessing for SQL Server-specific syntax
      try {
        const processedSql = this.preprocessSqlServerSyntax(sql);
        this.parser.astify(processedSql, { database: dbType });
        return {
          isValid: true,
          error: '',
        };
      } catch (preprocessError: any) {
        return {
          isValid: false,
          error: `SQL Syntax Error: ${error.message || 'Invalid SQL syntax'}`,
        };
      }
    }
  }

  /**
   * Validates multiple SQL statements
   * @param sqlStatements - Array of SQL query strings
   * @param database - The database type to validate against
   * @returns Array of validation results
   */
  validateMultiple(sqlStatements: string[], database?: string): SqlValidationResult[] {
    return sqlStatements.map((sql) => this.validateSyntax(sql, database));
  }

  /**
   * Checks if a SQL query is a SELECT statement
   * @param sql - The SQL query string
   * @returns boolean indicating if it's a SELECT statement
   */
  isSelectStatement(sql: string): boolean {
    const trimmed = sql.trim().toLowerCase();
    return trimmed.startsWith('select');
  }

  /**
   * Checks if a SQL query contains specific keywords
   * @param sql - The SQL query string
   * @param keywords - Array of keywords to check for
   * @returns boolean indicating if any keywords are found
   */
  containsKeywords(sql: string, keywords: string[]): boolean {
    const lowerSql = sql.toLowerCase();
    return keywords.some((keyword) => lowerSql.includes(keyword.toLowerCase()));
  }
}

// Export a singleton instance configured for SQL Server
export const sqlValidator = new SqlValidator('transactsql');
